/**
 * Google Analytics 4 with Consent Mode v2.
 *
 * The tag is injected from here rather than hardcoded into index.html for two
 * reasons: local development never pollutes the property with fake traffic, and
 * page views are sent manually so React Router navigations are recorded with the
 * right URL and title (a single-page app only ever "loads" once).
 *
 * Consent: analytics storage starts DENIED, as UK GDPR / PECR require. Until the
 * visitor accepts, gtag sends cookieless pings only. CookieConsent.jsx calls
 * setAnalyticsConsent() to flip it.
 *
 * The measurement ID is public by design - it identifies the property, it isn't
 * a credential.
 */
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_ID || 'G-GNJ9V2MGP5';

const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]', ''];
export const CONSENT_KEY = 'el_cookie_consent'; // 'granted' | 'denied'

/** Off on localhost, so dev traffic never reaches the live property. */
export const analyticsEnabled = () =>
  typeof window !== 'undefined' &&
  Boolean(GA_MEASUREMENT_ID) &&
  !LOCAL_HOSTS.includes(window.location.hostname);

/** Reading localStorage throws in some privacy modes - never let that break the page. */
export function getStoredConsent() {
  try {
    return window.localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

const gtag = (...args) => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  window.dataLayer.push(args);
};

/** Loads gtag.js once. Safe to call repeatedly. */
export function initAnalytics() {
  if (!analyticsEnabled() || window.__gaLoaded) return;
  window.__gaLoaded = true;

  window.dataLayer = window.dataLayer || [];

  // Consent defaults MUST be pushed before the tag configures itself.
  // We run no advertising, so the ad_* signals stay denied permanently.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: getStoredConsent() === 'granted' ? 'granted' : 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  gtag('js', new Date());
  // send_page_view:false - views are sent per route change in trackPageView().
  gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
}

/** Records the visitor's choice and tells gtag about it. */
export function setAnalyticsConsent(granted) {
  try {
    window.localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied');
  } catch {
    // Private mode - the choice just won't persist between visits.
  }

  if (!analyticsEnabled()) return;
  gtag('consent', 'update', { analytics_storage: granted ? 'granted' : 'denied' });

  // The first page view happened before consent existed; send it now.
  if (granted) trackPageView(window.location.pathname + window.location.search);
}

/** One page_view for the current route. */
export function trackPageView(path, title) {
  if (!analyticsEnabled()) return;
  gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title || document.title,
  });
}

/** Any GA4 event. */
export function trackEvent(name, params = {}) {
  if (!analyticsEnabled()) return;
  gtag('event', name, params);
}

/* ------------------------------ ecommerce ------------------------------- */
/* GA4 expects a specific `items` shape; these helpers keep call sites short
   and stop the field names drifting apart between events.                   */

const toItem = (i, index) => ({
  item_id: String(i.id ?? i.item_id ?? ''),
  item_name: i.name ?? i.item_name ?? '',
  item_variant: i.variant ?? undefined,
  item_category: i.category ?? undefined,
  price: Number(i.price ?? 0),
  quantity: Number(i.quantity ?? 1),
  index,
});

export const trackViewItem = (product) =>
  trackEvent('view_item', {
    currency: 'GBP',
    value: Number(product.price || 0),
    items: [toItem(product, 0)],
  });

export const trackAddToCart = (product, quantity = 1) =>
  trackEvent('add_to_cart', {
    currency: 'GBP',
    value: Number(product.price || 0) * quantity,
    items: [toItem({ ...product, quantity }, 0)],
  });

export const trackBeginCheckout = (items, value) =>
  trackEvent('begin_checkout', {
    currency: 'GBP',
    value: Number(value || 0),
    items: items.map(toItem),
  });

export const trackPurchase = ({ orderId, total, shipping, items }) =>
  trackEvent('purchase', {
    transaction_id: orderId,
    currency: 'GBP',
    value: Number(total || 0),
    shipping: Number(shipping || 0),
    items: (items || []).map(toItem),
  });
